/* Header-only campaign; normal markup remains the failure fallback. */
(() => {
  const START = Date.UTC(2026, 9, 1, 5);
  const END = Date.UTC(2026, 10, 1, 5);
  class OctoberAwarenessLogo extends HTMLElement {
    connectedCallback() {
      this.generation = (this.generation || 0) + 1;
      this.controller = new AbortController();
      this.awareness = this.querySelector('[data-awareness-image]');
      this.loaded = false;
      this.failed = false;
      this.removeAttribute('data-active');
      if (!this.awareness || !['auto', 'on', 'off'].includes(this.dataset.mode)) return;
      this.awareness.addEventListener('error', () => {
        this.failed = true;
        this.removeAttribute('data-active');
      }, {signal: this.controller.signal});
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.refresh();
      }, {signal: this.controller.signal});
      this.refresh();
    }
    disconnectedCallback() {
      this.generation++;
      clearTimeout(this.timer);
      clearTimeout(this.expiryTimer);
      this.controller?.abort();
    }
    show(eligible) {
      this.toggleAttribute('data-active', Boolean(eligible && this.loaded && !this.failed));
    }
    async load() {
      if (this.loaded || this.failed) return;
      const image = this.awareness;
      try {
        image.loading = 'eager';
        if (!image.src) image.src = image.dataset.src;
        await image.decode();
        this.loaded = image.naturalWidth > 0;
      } catch { this.failed = true; }
    }
    async refresh() {
      clearTimeout(this.timer);
      const generation = ++this.generation;
      const mode = this.dataset.mode;
      if (mode === 'off' || this.failed || !['auto', 'on'].includes(mode)) {
        clearTimeout(this.expiryTimer);
        this.show(false);
        return;
      }
      if (mode === 'on') {
        await this.load();
        if (generation === this.generation) this.show(true);
        return;
      }
      try {
        const url = new URL(this.dataset.timeUrl, location.origin);
        if (url.origin !== location.origin) return;
        url.searchParams.set('october_logo_time', crypto.randomUUID());
        const sent = performance.now();
        const response = await fetch(url, {
          method: 'HEAD', cache: 'no-store', credentials: 'same-origin',
          signal: AbortSignal.timeout(4000)
        });
        const received = performance.now();
        const date = response.headers.get('date');
        const age = response.headers.get('age');
        const epoch = Date.parse(date);
        if (!response.ok || new URL(response.url).origin !== location.origin ||
            !date || !/GMT$/.test(date) || !Number.isFinite(epoch) ||
            (age !== null && age !== '0') || received - sent > 4000) throw new Error('Invalid server time');
        // Upper bound includes HTTP Date's one-second precision and round trip.
        const upper = epoch + 1000 + received - sent;
        if (epoch >= START && upper < END) await this.load();
        if (generation !== this.generation) return;
        const elapsed = performance.now() - received;
        const nowUpper = upper + elapsed;
        const eligible = epoch >= START && nowUpper < END && elapsed < 60000;
        clearTimeout(this.expiryTimer);
        this.show(eligible);
        if (eligible) this.expiryTimer = setTimeout(() => this.show(false), Math.max(1, Math.min(60000 - elapsed, END - nowUpper)));
        const untilStart = epoch < START ? Math.max(1000, START - nowUpper) : 55000;
        this.timer = setTimeout(() => this.refresh(), Math.min(55000, untilStart));
      } catch {
        if (generation !== this.generation) return;
        this.show(false);
        this.timer = setTimeout(() => this.refresh(), 60000);
      }
    }
  }
  if (!customElements.get('october-awareness-logo')) customElements.define('october-awareness-logo', OctoberAwarenessLogo);
})();
